import { useEffect, useState } from 'react';

import { get, RequestOptions } from './api';

type Props = {
  url: string;
  opts?: RequestOptions;
};

// Verifies that our useApi generic type <D> only contains valid json response types
type ValidApiResponse = {
  [key: string]: string | number | boolean | ValidApiResponse | Array<ValidApiResponse> | null;
};

/**
 * Unmount safe hook for making simple requests to our backend.
 * Use this hook when you need to fetch something
 * from our server but do not need to persist the data.
 *
 * @see server responses should be in `json` format
 * @see Redux should be used for most data fetching and
 * this should only be used for one-time or small
 * usage requests.
 */
export const useApi = <D extends ValidApiResponse | ValidApiResponse[]>({ url, opts }: Props) => {
  const [data, setData] = useState<D | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let unmounted = false;
    const unmountSafeSetState = <T,>(setState: (v: T) => void, value: T) => {
      if (!unmounted) {
        setState(value);
      }
    };

    const callUrl = async () => {
      const defaultError = 'Failed to load data';
      try {
        const response = await get(url, opts);
        const contentType = response.headers.get('content-type');
        if (!response.ok) {
          let errorResponse;
          if (contentType?.includes('application/json')) {
            errorResponse = (await response.json())?.errorMessage;
          } else {
            errorResponse = await response.text();
          }
          unmountSafeSetState(setErrorMessage, errorResponse ?? defaultError);
          return;
        }
        const json = await response.json();
        unmountSafeSetState(setData, json);
      } catch {
        unmountSafeSetState(setErrorMessage, defaultError);
      }
      unmountSafeSetState(setLoading, false);
    };

    if (loading) {
      callUrl();
    }

    return () => {
      unmounted = true;
    };
  }, [url, loading, opts]);

  const refetch = () => {
    setLoading(true);
  };

  return {
    data,
    errorMessage,
    loading,
    refetch,
  };
};
